/* ═══════════════════════════════════════════════════════════════
   HarSyncService — RWB-only bridge: SpecForma_HAR_Sync.xlsx → SpecForma.

   The Power Automate flow (SF_All_Sync) rewrites four clean tables in the
   Sync workbook every week: PROJECTS / PEOPLE / DELIVERABLES / ASSIGNMENTS.
   This service reads that workbook (local OneDrive path or share URL via the
   main-process bridge) and applies the agreed ADD-ONLY semantics:

     • projects / deliverables / assignments: add if missing, else skip —
       never update, never delete, never touch active/inactive.
     • forecasts: the ONE place that "replaces" — HAR weekly hours overwrite
       the manual day-cells for that week for each (member, project) pair
       present in the workbook. Pairs not in the HAR are left alone.

   TWO-STEP FLOW: computeHarSyncPlan() is READ-ONLY and returns everything
   that WOULD change; nothing is written until applyHarSyncPlan(plan).
   Startup only computes + notifies unless the auto-apply pref is on.

   Gated to Josh's account (HAR_SYNC_EMAIL) and to the desktop app.
   ═══════════════════════════════════════════════════════════════ */

import ExcelJS from 'exceljs';
import type { Project, Deliverable, TeamMember } from '../../shared/types';
import { ProjectService } from './projectService';
import { TeamService } from './teamService';
import { TimeService, addDays } from './timeService';
import * as cache from './cloudCache';

export const HAR_SYNC_EMAIL = 'jbeardsley@rwb.net';

/** Workbook source + auto-apply are machine-specific, persisted to a JSON
 *  file in appData via IPC (har:loadPrefs/savePrefs) so they survive app
 *  updates and reinstalls. Old localStorage values migrate on first read. */
const LEGACY_SOURCE_KEY = 'sf.harSync.source';
const LEGACY_AUTO_KEY = 'sf.harSync.autoApply';

interface HarPrefs {
  source?: string;
  autoApply?: boolean;
}
let prefsCache: HarPrefs | null = null;

async function loadPrefs(): Promise<HarPrefs> {
  if (prefsCache) return prefsCache;
  let p: HarPrefs = {};
  try {
    p = (await window.api.loadHarPrefs()) || {};
  } catch {
    p = {};
  }
  // One-time migration from the old localStorage keys.
  try {
    let dirty = false;
    const legacySource = localStorage.getItem(LEGACY_SOURCE_KEY);
    if (!p.source && legacySource) {
      p.source = legacySource;
      dirty = true;
    }
    if (p.autoApply === undefined && localStorage.getItem(LEGACY_AUTO_KEY) === '1') {
      p.autoApply = true;
      dirty = true;
    }
    if (dirty) void window.api.saveHarPrefs(p);
  } catch {
    /* storage disabled — ignore */
  }
  prefsCache = p;
  return p;
}
async function savePrefs(patch: HarPrefs): Promise<void> {
  const p = { ...(await loadPrefs()), ...patch };
  prefsCache = p;
  try {
    await window.api.saveHarPrefs(p);
  } catch {
    /* ignore — cache still holds it for this session */
  }
}

export async function getHarSyncSource(): Promise<string> {
  return (await loadPrefs()).source || '';
}
export async function setHarSyncSource(source: string): Promise<void> {
  await savePrefs({ source: source.trim() });
}
export async function getHarAutoApply(): Promise<boolean> {
  return !!(await loadPrefs()).autoApply;
}
export async function setHarAutoApply(v: boolean): Promise<void> {
  await savePrefs({ autoApply: v });
}

/** RWB bridge gate: Josh's account, desktop app only (web shim throws). */
export function canHarSync(user: { email?: string } | null | undefined): boolean {
  if (!user?.email || user.email.toLowerCase() !== HAR_SYNC_EMAIL) return false;
  return typeof navigator !== 'undefined' && navigator.userAgent.includes('Electron');
}

// ─── Plan (read-only preview) ────────────────────────────────────────

export interface HarSyncPlan {
  weekStart: string;
  newProjects: { projectNo: string; name: string; client: string; picId?: string; pmId?: string }[];
  newDeliverables: { projectNo: string; name: string; dueDate: string }[];
  newAssignments: { projectNo: string; memberId: string; memberName: string; role: string }[];
  /** Forecast day-cells whose value would change (includes 0-hour deletes). */
  forecastCells: { projectNo: string; memberId: string; day: string; hours: number }[];
  /** Per member+project weekly totals, for the human-readable report. */
  forecastPairsList: { memberId: string; memberName: string; projectNo: string; hours: number }[];
  skippedNoEmail: string[];
  skippedNoMember: string[];
  skippedTbd: number;
  /** Total write operations Apply would perform. */
  total: number;
}

export interface HarSyncSummary {
  weekStart: string;
  projectsAdded: number;
  deliverablesAdded: number;
  assignmentsAdded: number;
  forecastCellsSet: number;
  errors: string[];
}

// Same guard as the Extract_HAR Office Script — belt and suspenders in case an
// older workbook (pre-TBD-filter) is synced.
const isTBD = (s: string): boolean => /^\s*tbd\b/i.test(s);

interface WbProject {
  projectNo: string;
  name: string;
  client: string;
  picEmails: string[];
  pmEmails: string[];
}
interface WbDeliverable {
  projectNo: string;
  name: string;
  dueDate: string;
  /** Raw date-cell text, pre-normalization — lets the TBD guard see "TBD". */
  dueRaw: string;
}
interface WbAssignment {
  projectNo: string;
  email: string;
  harToken: string;
  discipline: string;
  role: string;
  hours: number;
}
interface WbData {
  weekStart: string;
  projects: WbProject[];
  deliverables: WbDeliverable[];
  assignments: WbAssignment[];
}

/** Cell value → trimmed string; date cells → YYYY-MM-DD. */
function cellText(v: ExcelJS.CellValue): string {
  if (v === null || v === undefined) return '';
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v === 'object') {
    const o = v as unknown as Record<string, unknown>;
    if (typeof o.text === 'string') return o.text.trim();
    if (Array.isArray(o.richText)) {
      return o.richText
        .map((r: { text?: string }) => r.text || '')
        .join('')
        .trim();
    }
    if (o.result !== undefined) return cellText(o.result as ExcelJS.CellValue);
    return '';
  }
  return String(v).trim();
}

/** Date column → YYYY-MM-DD; tolerates Excel serials; '' for anything else. */
function toIsoDate(v: ExcelJS.CellValue): string {
  const s = cellText(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const n = Number(s);
  if (!isNaN(n) && n > 20000 && n < 60000) {
    // Excel serial date (25569 = 1970-01-01)
    return new Date(Math.round((n - 25569) * 86400 * 1000)).toISOString().slice(0, 10);
  }
  return '';
}

/** Read one sheet's table: header row 1 → array of {header: string} rows. */
function readSheet(wb: ExcelJS.Workbook, name: string): Record<string, ExcelJS.CellValue>[] {
  const sheet = wb.getWorksheet(name);
  if (!sheet) throw new Error(`Workbook is missing the ${name} sheet.`);
  const headers: string[] = [];
  sheet.getRow(1).eachCell((cell, col) => {
    headers[col] = cellText(cell.value);
  });
  const rows: Record<string, ExcelJS.CellValue>[] = [];
  sheet.eachRow((row, rowNum) => {
    if (rowNum === 1) return;
    const rec: Record<string, ExcelJS.CellValue> = {};
    let hasValue = false;
    row.eachCell((cell, col) => {
      const h = headers[col];
      if (!h) return;
      rec[h] = cell.value;
      if (cellText(cell.value)) hasValue = true;
    });
    if (hasValue) rows.push(rec);
  });
  return rows;
}

const splitEmails = (v: ExcelJS.CellValue): string[] =>
  cellText(v)
    .split(/[;,]/)
    .map((e) => e.trim().toLowerCase())
    .filter((e) => e.includes('@'));

export function parseWorkbook(wb: ExcelJS.Workbook): WbData {
  const projRows = readSheet(wb, 'PROJECTS');
  const delivRows = readSheet(wb, 'DELIVERABLES');
  const asnRows = readSheet(wb, 'ASSIGNMENTS');

  const projects: WbProject[] = projRows
    .map((r) => ({
      projectNo: cellText(r.ProjectNo),
      name: cellText(r.ProjectName),
      client: cellText(r.Client),
      picEmails: splitEmails(r.PIC_Email),
      pmEmails: splitEmails(r.PM_Email),
    }))
    .filter((p) => /^\d{5}-\d{2}$/.test(p.projectNo));

  const deliverables: WbDeliverable[] = delivRows
    .map((r) => ({
      projectNo: cellText(r.ProjectNo),
      name: cellText(r.Name),
      dueDate: toIsoDate(r.DueDate),
      dueRaw: cellText(r.DueDate),
    }))
    .filter((d) => d.projectNo && d.name);

  const assignments: WbAssignment[] = asnRows
    .map((r) => ({
      projectNo: cellText(r.ProjectNo),
      email: cellText(r.Email).toLowerCase(),
      harToken: cellText(r.HARToken),
      discipline: cellText(r.Discipline),
      role: cellText(r.Role),
      hours: Number(cellText(r.Hours)) || 0,
    }))
    .filter((a) => a.projectNo);

  let weekStart = '';
  for (const r of [...asnRows, ...projRows]) {
    weekStart = toIsoDate(r.WeekStart);
    if (weekStart) break;
  }
  return { weekStart, projects, deliverables, assignments };
}

/** Weekly hours → 5 Mon–Fri cells in quarter-hour steps; sums to round(h*4)/4. */
export function splitWeekHours(hours: number): number[] {
  const quarters = Math.round(hours * 4);
  const per = Math.floor(quarters / 5);
  const extra = quarters % 5;
  return Array.from({ length: 5 }, (_, i) => (per + (i < extra ? 1 : 0)) / 4);
}

/** Small worker pool — keeps bulk writes under the API rate limit. */
async function pool<T>(items: T[], limit: number, fn: (item: T) => Promise<void>): Promise<void> {
  let i = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (i < items.length) {
      const item = items[i++];
      await fn(item);
    }
  });
  await Promise.all(workers);
}

const normNo = (s: string): string => s.trim().replace(/\./g, '-').toUpperCase();
const roleFor = (r: string): string =>
  r.toUpperCase() === 'PIC' ? 'pic' : r.toUpperCase() === 'PM' ? 'pm' : 'member';

/**
 * READ-ONLY preview: parse the workbook, diff against live SpecForma, and
 * return everything Apply would write. Returns null when no source is
 * configured on this machine.
 */
export async function computeHarSyncPlan(
  onProgress?: (msg: string) => void
): Promise<HarSyncPlan | null> {
  const source = await getHarSyncSource();
  if (!source) return null;
  const progress = (m: string) => onProgress?.(m);

  progress('Reading workbook…');
  const raw = await window.api.readHarWorkbook(source);
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(raw as unknown as ArrayBuffer);
  const data = parseWorkbook(wb);

  progress('Comparing against SpecForma…');
  const [projects, members, assignments, allDelivs] = await Promise.all([
    ProjectService.list(),
    TeamService.list(),
    TeamService.listAssignments(),
    cache.list<Deliverable>('deliverables'),
  ]);

  const projByNo = new Map<string, Project>();
  for (const p of projects) if (p.projectNumber) projByNo.set(normNo(p.projectNumber), p);
  const memberByEmail = new Map<string, TeamMember>();
  for (const m of members) if (m.email) memberByEmail.set(m.email.toLowerCase(), m);
  const memberByName = new Map<string, TeamMember>();
  for (const m of members) if (m.name) memberByName.set(m.name.trim().toLowerCase(), m);
  // PEOPLE_MAP sometimes carries a NAME in the Email column (data-entry slip,
  // e.g. "michael danielson"); fall back to an exact name match so those rows
  // still resolve to the right member.
  const resolveMember = (emailOrName: string): TeamMember | undefined =>
    memberByEmail.get(emailOrName) ||
    (emailOrName && !emailOrName.includes('@') ? memberByName.get(emailOrName.trim()) : undefined);
  const asnKeys = new Set(assignments.map((a) => a.projectId + '|' + a.teamMemberId));
  const delivKeys = new Set(allDelivs.map((d) => d.projectId + '|' + d.name.trim().toLowerCase()));

  const plan: HarSyncPlan = {
    weekStart: data.weekStart,
    newProjects: [],
    newDeliverables: [],
    newAssignments: [],
    forecastCells: [],
    forecastPairsList: [],
    skippedNoEmail: [],
    skippedNoMember: [],
    skippedTbd: 0,
    total: 0,
  };

  // Projects
  const plannedNos = new Set<string>();
  for (const hp of data.projects) {
    const key = normNo(hp.projectNo);
    if (projByNo.has(key) || plannedNos.has(key)) continue;
    plannedNos.add(key);
    plan.newProjects.push({
      projectNo: hp.projectNo,
      name: hp.name || hp.projectNo,
      client: hp.client,
      picId: hp.picEmails.map((e) => memberByEmail.get(e)?.id).find(Boolean),
      pmId: hp.pmEmails.map((e) => memberByEmail.get(e)?.id).find(Boolean),
    });
  }
  const projectKnown = (no: string) => projByNo.has(normNo(no)) || plannedNos.has(normNo(no));

  // Deliverables (dedupe by projectId+name for existing projects; by
  // projectNo+name for planned-new ones)
  const plannedDelivKeys = new Set<string>();
  for (const d of data.deliverables) {
    if (isTBD(d.name) || isTBD(d.dueRaw)) {
      plan.skippedTbd++;
      continue;
    }
    if (!projectKnown(d.projectNo)) continue;
    const proj = projByNo.get(normNo(d.projectNo));
    const key = (proj ? proj.id : normNo(d.projectNo)) + '|' + d.name.trim().toLowerCase();
    if ((proj && delivKeys.has(key)) || plannedDelivKeys.has(key)) continue;
    plannedDelivKeys.add(key);
    plan.newDeliverables.push({ projectNo: d.projectNo, name: d.name, dueDate: d.dueDate });
  }

  // Assignments
  const noEmailSeen = new Set<string>();
  const noMemberSeen = new Set<string>();
  const plannedAsnKeys = new Set<string>();
  for (const a of data.assignments) {
    if (!projectKnown(a.projectNo)) continue;
    if (!a.email) {
      const label = `${a.discipline} | ${a.harToken}`;
      if (!noEmailSeen.has(label)) {
        noEmailSeen.add(label);
        plan.skippedNoEmail.push(label);
      }
      continue;
    }
    const member = resolveMember(a.email);
    if (!member) {
      const label = a.email.includes('@')
        ? a.email
        : `"${a.email}" (name in the PEOPLE_MAP Email column — fix the map)`;
      if (!noMemberSeen.has(label)) {
        noMemberSeen.add(label);
        plan.skippedNoMember.push(label);
      }
      continue;
    }
    const proj = projByNo.get(normNo(a.projectNo));
    const key = (proj ? proj.id : normNo(a.projectNo)) + '|' + member.id;
    if ((proj && asnKeys.has(key)) || plannedAsnKeys.has(key)) continue;
    plannedAsnKeys.add(key);
    plan.newAssignments.push({
      projectNo: a.projectNo,
      memberId: member.id,
      memberName: member.name || a.email,
      role: roleFor(a.role),
    });
  }

  // Forecasts — weekly totals per member+project, diffed against the week's
  // existing manual day-cells so unchanged cells never re-write.
  if (data.weekStart) {
    const pairHours = new Map<
      string,
      { projectNo: string; memberId: string; memberName: string; hours: number }
    >();
    for (const a of data.assignments) {
      if (a.hours <= 0 || !a.email) continue;
      if (!projectKnown(a.projectNo)) continue;
      const member = resolveMember(a.email);
      if (!member) continue;
      const key = member.id + '|' + normNo(a.projectNo);
      const prev = pairHours.get(key);
      if (prev) prev.hours += a.hours;
      else
        pairHours.set(key, {
          projectNo: a.projectNo,
          memberId: member.id,
          memberName: member.name || a.email,
          hours: a.hours,
        });
    }
    const weekDays = Array.from({ length: 5 }, (_, i) => addDays(data.weekStart, i));
    const existing = await TimeService.listForecastsForRange(weekDays[0], weekDays[4]);
    const cellHours = new Map<string, number>();
    for (const f of existing) {
      if (f.sourceKind === 'vacation') continue;
      cellHours.set(f.teamMemberId + '|' + f.projectId + '|' + f.dayDate, f.hours);
    }
    for (const pair of pairHours.values()) {
      const proj = projByNo.get(normNo(pair.projectNo));
      const perDay = splitWeekHours(pair.hours);
      let changed = false;
      weekDays.forEach((day, i) => {
        const key = pair.memberId + '|' + (proj ? proj.id : '∅') + '|' + day;
        if ((cellHours.get(key) || 0) !== perDay[i]) {
          changed = true;
          plan.forecastCells.push({
            projectNo: pair.projectNo,
            memberId: pair.memberId,
            day,
            hours: perDay[i],
          });
        }
      });
      if (changed)
        plan.forecastPairsList.push({
          memberId: pair.memberId,
          memberName: pair.memberName,
          projectNo: pair.projectNo,
          hours: pair.hours,
        });
    }
  }

  plan.total =
    plan.newProjects.length +
    plan.newDeliverables.length +
    plan.newAssignments.length +
    plan.forecastCells.length;
  progress('');
  return plan;
}

/**
 * Write an approved plan. Re-checks existence per row (safe if run twice or
 * if the data moved between preview and apply) — still strictly add-only.
 */
export async function applyHarSyncPlan(
  plan: HarSyncPlan,
  onProgress?: (msg: string) => void
): Promise<HarSyncSummary> {
  const progress = (m: string) => onProgress?.(m);
  const summary: HarSyncSummary = {
    weekStart: plan.weekStart,
    projectsAdded: 0,
    deliverablesAdded: 0,
    assignmentsAdded: 0,
    forecastCellsSet: 0,
    errors: [],
  };
  const errCounts = new Map<string, number>();
  const fail = (what: string, err: unknown) => {
    const msg = `${what}: ${err instanceof Error ? err.message : String(err)}`;
    errCounts.set(msg, (errCounts.get(msg) || 0) + 1);
    console.warn('[harSync]', msg);
  };

  const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
  const isRateLimit = (err: unknown): boolean =>
    /rate limit|too many|429/i.test(err instanceof Error ? err.message : String(err));
  /** One write, paced + rate-limit-safe. The API allows 2000 req/min/user; a
   *  big first-run sync (2000+ cells) blows through that at full speed, so
   *  every write carries a small delay (~19 req/s across 4 workers) and, if
   *  the limiter still trips, waits out the 60s window and retries. */
  let backoffUntil = 0;
  const paced = async <T>(fn: () => Promise<T>): Promise<T> => {
    for (let attempt = 0; ; attempt++) {
      const wait = backoffUntil - Date.now();
      if (wait > 0) await sleep(wait);
      try {
        const out = await fn();
        await sleep(60);
        return out;
      } catch (err) {
        if (isRateLimit(err) && attempt < 2) {
          progress('API rate limit hit — pausing 65s, then resuming…');
          backoffUntil = Math.max(backoffUntil, Date.now() + 65_000);
          continue;
        }
        throw err;
      }
    }
  };

  const [projects, assignments, allDelivs] = await Promise.all([
    ProjectService.list(),
    TeamService.listAssignments(),
    cache.list<Deliverable>('deliverables'),
  ]);
  const projByNo = new Map<string, Project>();
  for (const p of projects) if (p.projectNumber) projByNo.set(normNo(p.projectNumber), p);
  const asnKeys = new Set(assignments.map((a) => a.projectId + '|' + a.teamMemberId));
  const delivKeys = new Set(allDelivs.map((d) => d.projectId + '|' + d.name.trim().toLowerCase()));

  // 1) Projects first — everything else references them.
  progress(`Adding ${plan.newProjects.length} projects…`);
  await pool(plan.newProjects, 4, async (np) => {
    if (projByNo.has(normNo(np.projectNo))) return;
    try {
      const created = await paced(() =>
        ProjectService.save({
          id: '',
          name: np.name,
          projectNumber: np.projectNo,
          clientName: np.client || undefined,
          status: 'active',
          picId: np.picId,
          pmId: np.pmId,
          selections: {},
          createdAt: '',
          updatedAt: '',
        } as Project)
      );
      projByNo.set(normNo(np.projectNo), created);
      summary.projectsAdded++;
    } catch (err) {
      fail(`project ${np.projectNo}`, err);
    }
  });

  // 2) Deliverables + assignments
  progress(`Adding ${plan.newDeliverables.length} deliverables…`);
  await pool(plan.newDeliverables, 4, async (d) => {
    const proj = projByNo.get(normNo(d.projectNo));
    if (!proj) return;
    const key = proj.id + '|' + d.name.trim().toLowerCase();
    if (delivKeys.has(key)) return;
    delivKeys.add(key);
    try {
      await paced(() =>
        TimeService.saveDeliverable({
          id: '',
          projectId: proj.id,
          name: d.name,
          dueDate: d.dueDate || undefined,
          status: 'pending',
          issued: false,
          createdAt: '',
          updatedAt: '',
        } as Deliverable)
      );
      summary.deliverablesAdded++;
    } catch (err) {
      fail(`deliverable ${d.projectNo} ${d.name}`, err);
    }
  });

  progress(`Adding ${plan.newAssignments.length} assignments…`);
  await pool(plan.newAssignments, 4, async (a) => {
    const proj = projByNo.get(normNo(a.projectNo));
    if (!proj) return;
    const key = proj.id + '|' + a.memberId;
    if (asnKeys.has(key)) return;
    asnKeys.add(key);
    try {
      await paced(() => TeamService.assign(proj.id, a.memberId, a.role));
      summary.assignmentsAdded++;
    } catch (err) {
      fail(`assignment ${a.memberName} → ${a.projectNo}`, err);
    }
  });

  // 3) Forecast day-cells (upsert; hours<=0 deletes — idempotent)
  const totalCells = plan.forecastCells.length;
  let cellsDone = 0;
  progress(`Writing ${totalCells} forecast cells…`);
  await pool(plan.forecastCells, 4, async (c) => {
    const proj = projByNo.get(normNo(c.projectNo));
    if (!proj) return;
    try {
      await paced(() =>
        TimeService.setForecastDayCell({
          teamMemberId: c.memberId,
          projectId: proj.id,
          dayDate: c.day,
          hours: c.hours,
          notes: 'HAR sync',
        })
      );
      summary.forecastCellsSet++;
    } catch (err) {
      fail('forecast cell', err);
    }
    cellsDone++;
    if (cellsDone % 25 === 0) progress(`Writing forecast cells… ${cellsDone}/${totalCells}`);
  });

  summary.errors = [...errCounts.entries()]
    .slice(0, 10)
    .map(([m, c]) => (c > 1 ? `${m} (×${c})` : m));
  progress('');
  return summary;
}
